import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Send, Trash2, AlertCircle } from 'lucide-react';
import RequirePermission from '@shared/components/RequirePermission';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import { notesApi } from '../services/notesApi';
import type { ClinicDetailDto } from '../types/clinic';
import './ClinicInfoSection.css';
import './ClinicNotesSection.css';

interface ClinicNotesSectionProps {
  clinic: ClinicDetailDto;
}

function formatDate(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/**
 * General, free-form notes on the clinic profile. Separate from relationship
 * notes (which are scoped to a specific client-clinic relationship); these are
 * clinic-wide notes that any viewer of the profile can reference.
 */
export default function ClinicNotesSection({ clinic }: Readonly<ClinicNotesSectionProps>) {
  const queryClient = useQueryClient();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);

  const notesKey = ['clinicNotes', clinic.id];

  const { data: notes = [], isLoading, isError } = useQuery({
    queryKey: notesKey,
    queryFn: () => notesApi.getClinicNotes(clinic.id),
    enabled: clinic.id > 0,
  });

  const createMutation = useMutation({
    mutationFn: (text: string) => notesApi.createClinicNote(clinic.id, text),
    onSuccess: () => {
      setNewNoteText('');
      queryClient.invalidateQueries({ queryKey: notesKey });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: number) => notesApi.deleteNote(noteId),
    onSuccess: () => {
      setNoteToDelete(null);
      queryClient.invalidateQueries({ queryKey: notesKey });
    },
  });

  const handleAdd = () => {
    const text = newNoteText.trim();
    if (!text) return;
    createMutation.mutate(text);
  };

  const actionError = createMutation.error || deleteMutation.error;

  return (
    <div className="info-section">
      <div className="info-section-header">
        <button
          type="button"
          className="info-section-toggle"
          onClick={() => setIsCollapsed(c => !c)}
          aria-expanded={!isCollapsed}
        >
          <ChevronDown size={16} className={`info-section-chevron ${isCollapsed ? 'collapsed' : ''}`} />
          <h3 className="info-section-title">Notes ({notes.length})</h3>
        </button>
      </div>

      {!isCollapsed && (
        <div className="clinic-notes-body">
          {actionError && (
            <div className="info-save-error">
              <AlertCircle size={14} />
              {(actionError as Error).message || 'Something went wrong with notes.'}
            </div>
          )}

          <RequirePermission permission="UserCanCreateClinicNote">
            <div className="clinic-notes-add-row">
              <textarea
                className="clinic-notes-add-input"
                value={newNoteText}
                onChange={e => setNewNoteText(e.target.value)}
                placeholder="Add a general note about this clinic..."
                rows={2}
                aria-label="New clinic note"
              />
              <button
                type="button"
                className="clinic-notes-add-btn"
                onClick={handleAdd}
                disabled={!newNoteText.trim() || createMutation.isPending}
                title="Add note"
                aria-label="Add note"
              >
                <Send size={16} />
              </button>
            </div>
          </RequirePermission>

          <div className="clinic-notes-list">
            {isLoading ? (
              <div className="clinic-notes-empty">Loading notes...</div>
            ) : isError ? (
              <div className="clinic-notes-empty clinic-notes-error">
                Couldn&apos;t load notes. Please refresh to try again.
              </div>
            ) : notes.length === 0 ? (
              <div className="clinic-notes-empty">No notes yet</div>
            ) : (
              notes.map(note => (
                <div key={note.id} className="clinic-note-item">
                  <div className="clinic-note-item-header">
                    <span className="clinic-note-date">{formatDate(note.createdAt)}</span>
                    {note.createdByName && (
                      <span className="clinic-note-author">{note.createdByName}</span>
                    )}
                    <RequirePermission permission="UserCanDeleteClinicNote">
                      <button
                        type="button"
                        className="clinic-note-delete-btn"
                        onClick={() => setNoteToDelete(note.id)}
                        disabled={deleteMutation.isPending}
                        title="Delete note"
                        aria-label="Delete note"
                      >
                        <Trash2 size={14} />
                      </button>
                    </RequirePermission>
                  </div>
                  <div className="clinic-note-text">{note.noteText}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={noteToDelete !== null}
        title="Delete note"
        message="Are you sure you want to delete this note? This action can't be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (noteToDelete !== null) deleteMutation.mutate(noteToDelete);
        }}
        onCancel={() => setNoteToDelete(null)}
      />
    </div>
  );
}
